import React, { useState } from "react";
import { api, API } from "@/lib/api";
import { FileDown, Mail, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function MemoExportButtons({ opp }) {
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [to, setTo] = useState("");
  const [emailing, setEmailing] = useState(false);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("co_token");
      const res = await fetch(`${API}/opportunities/${opp.id}/memo.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `memo-${opp.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Memo PDF downloaded");
    } catch (e) {
      toast.error("PDF download failed");
    } finally { setBusy(false); }
  };

  const sendMemo = async (e) => {
    e.preventDefault();
    if (!to.trim()) return;
    setEmailing(true);
    try {
      const r = await api.post(`/opportunities/${opp.id}/memo/email`, { to });
      if (r.data.disabled) toast.warning("Email disabled (no Resend key)");
      else if (r.data.error) toast.error(`Email failed: ${r.data.error}`);
      else toast.success(`Memo emailed to ${to}`);
      setEmailOpen(false);
      setTo("");
    } catch (e) { toast.error("Email failed"); }
    finally { setEmailing(false); }
  };

  return (
    <>
      <button onClick={downloadPdf} disabled={busy} data-testid="export-memo-pdf" className="flex items-center gap-2 px-4 py-2 rounded border border-[#33291f] text-[13px] hover:border-[#d4a574] hover:text-[#d4a574] disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Export Memo PDF
      </button>
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogTrigger asChild>
          <button data-testid="email-memo-btn" className="flex items-center gap-2 px-4 py-2 rounded border border-[#33291f] text-[13px] hover:border-[#d4a574] hover:text-[#d4a574]">
            <Mail size={14} /> Email Memo
          </button>
        </DialogTrigger>
        <DialogContent className="bg-[#1c1712] border-[#33291f] text-[#f4ede2]">
          <DialogHeader><DialogTitle className="font-display">Email Investment Memo</DialogTitle></DialogHeader>
          <form onSubmit={sendMemo} className="space-y-3" data-testid="email-memo-form">
            <div className="text-[13px] text-[#b8ac96]">Send the AI-generated investment memo (PDF attachment) via Resend.</div>
            <Input required type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="investor@company.com" data-testid="email-memo-to" className="bg-[#241d16] border-[#33291f]" />
            <button type="submit" disabled={emailing} data-testid="email-memo-submit" className="w-full py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold disabled:opacity-60">
              {emailing ? "Sending…" : "Send Memo"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
